/**
 * QUIÉN ESCRIBE EN NEGOCIACIONES COMERCIALES, Y QUIÉN SOLO MIRA.
 *
 * ── DE DÓNDE SALE (2026-09-08) ──────────────────────────────────────────────
 * La usuaria: «debe poder abrir la negociación para ver qué productos debe
 * entregar», hablando de ALMACENYDESPACHO, que desde db/056 recibe por correo
 * las negociaciones cuya aplicación mueve mercancía («Productos», «Franelas» o
 * «Mobiliario (marketing)») justamente «para que ellos despachen esos productos
 * al asesor». Hasta hoy recibía el aviso y el botón «Abrir en el portal» lo
 * mandaba a un 403: su rol solo alcanza los módulos `almacen` e `info`, y la
 * página `nego` no colgaba de ninguno de los dos.
 *
 * `db/140` le da la página colgándola de `almacen`. Este módulo es la OTRA
 * mitad: Almacén **mira** el registro, no lo escribe. Nadie pidió que despachar
 * incluyera cargar solicitudes, aprobarlas ni subir evidencias.
 *
 * ── POR QUÉ AQUÍ Y NO EN LA PÁGINA ──────────────────────────────────────────
 * Es el patrón de `plan-permisos.ts` (db/107), y por el mismo motivo: el
 * esquema concede PÁGINAS, y «ver el registro» y «cargar una solicitud» son la
 * misma página. Así que el candado vive en el código y lo leen DOS lados:
 *
 *  · `netlify/functions/negociaciones.mts` — el que MANDA. El 403 va **una sola
 *    vez, junto al `requiereSesion`**, contra cualquier POST del módulo. No en
 *    cada rama: hoy son cinco (alta, decisión, anulación, evidencia y la NC) y
 *    la sexta que se añada mañana entraría sin candado sin que nadie lo notara.
 *  · `src/paginas/Negociaciones.tsx` — esconde lo que va a rebotar: a quien solo
 *    mira le deja UNA pestaña, el registro, y le quita el botón de subir
 *    evidencia de la fila desplegada.
 *
 * ── LA LISTA ES POSITIVA (los que YA escribían), no una lista de vetados ────
 * Son EXACTAMENTE los roles que hoy alcanzan la página `nego`: ONCE medidos
 * contra la base el 2026-09-08, y DOCE desde db/164 (2026-09-17), que añade
 * `ANALISTATRADEMKT`. Desde db/193 (2026-09-30) son DIEZ: TRADEMARKETING y
 * ANALISTATRADEMKT pasan a mirar, por decisión de la usuaria — ver
 * `ROLES_SOLO_MIRAN_NEGO`. Y desde el 2026-10-15 vuelven a ser ONCE:
 * TRADEMARKETING recupera la carga (ver su línea). Escrita así no le quita la escritura a NADIE
 * que la tuviera, y **falla CERRADO**: el rol que entre mañana al módulo
 * `almacen` —o a `comercial`, o a `cei`— mira el registro y no escribe hasta
 * que alguien lo ponga aquí. Con la lista al revés («los que no escriben»)
 * pasaría lo contrario, que es el error caro.
 *
 * El control 3.3 de `db/140` compara esta misma lista contra la base y falla si
 * un rol alcanza la página y no está escrito aquí: las dos listas no pueden
 * separarse en silencio. No es teoría — al primer intento cazó `GERENCIA`, que
 * alcanza la página por su módulo y tiene CERO usuarios, así que no aparecía en
 * ninguna cuenta hecha sobre personas.
 *
 * ⚠️ MASTER está dentro y tiene que estarlo: es quien anula.
 */
export const ROLES_ESCRIBEN_NEGO: readonly string[] = [
  'MASTER',
  'ASESOR',
  'REGIONAL',
  /* 🟢 TRADEMARKETING VUELVE el 2026-10-15. Salió el 2026-09-30 (db/193) junto
   * con ANALISTATRADEMKT, a mirar el registro; la usuaria, quince días
   * después: «habilitar la opcion de cargar negociaciones al usuario
   * trademarketing». Vuelve TAL COMO ESTABA antes de db/193: carga solicitudes
   * (también las de Farmatodo, cuya alta cierra `soloMiraNego`), sube
   * evidencias como cualquiera de esta lista (anular sigue siendo de MASTER),
   * y sigue decidiendo SOLO las de Trade (`rolQueToca`/`candadoDecision` en
   * el endpoint), nunca una que cargó él.
   *   · ANALISTATRADEMKT NO vuelve: el pedido nombra solo al usuario
   *     TRADEMARKETING. Sigue en `ROLES_SOLO_MIRAN_NEGO`.
   *   · En Negociaciones BBK sigue SOLO EL REGISTRO (db/245, del mismo día):
   *     esa lista manda por encima de ésta. Ver `puedeEscribirNegoEn`. */
  'TRADEMARKETING',
  'COBRANZAS',
  'ANALISTACEI',
  'GERENCIACEI',
  'GERENCIAVENTAS',
  'CONTRALORIA',
  'AUDIOVISUAL',
  /* CERO usuarios hoy, y por eso hay que decir por qué está: el rol GERENCIA
   * alcanza `nego` por su módulo, así que la cuenta que se cree mañana con él
   * escribiría igual que hoy. Sacarlo de aquí sería decidir en silencio que
   * Gerencia pasa a mirar — una decisión que nadie ha tomado. Lo cazó el
   * control 3.3 de db/140, que es exactamente para lo que está. */
  'GERENCIA',
];

/**
 * `true` si alguno de los roles del usuario escribe en Negociaciones.
 *
 * Basta UNO: quien sea Almacén y además MASTER escribe, porque lo que manda es
 * el rol que más puede, no el que menos. Al revés —exigir que TODOS escriban—
 * una cuenta con dos roles perdería lo que cada uno le da por separado.
 */
export function puedeEscribirNego(roles: readonly string[] | undefined): boolean {
  return (roles ?? []).some((r) => ROLES_ESCRIBEN_NEGO.includes(r));
}

/* ══════════════════════════════════════════════════════════════════════════
 * QUIÉN SOLO MIRA EL REGISTRO (2026-09-30, db/193)
 * ══════════════════════════════════════════════════════════════════════════
 * La usuaria: «los usuarios de IYD, TRADEMARKETING, ANALISTATRADESENIOR y
 * ANALISTATRADE [deben] ver la sección de negociaciones comerciales pero que
 * no puedan cargar o modificar negociaciones, solo que vean el registro».
 *
 * Son TRES roles (los dos analistas comparten `ANALISTATRADEMKT`, db/164) más
 * el de Almacén, que miraba desde db/140. Desde el 2026-10-15 son DOS más
 * Almacén: TRADEMARKETING vuelve a `ROLES_ESCRIBEN_NEGO` por pedido de la
 * usuaria («habilitar la opcion de cargar negociaciones al usuario
 * trademarketing»), y con eso recupera también el alta de Farmatodo. Medido ese día: ninguna de las
 * cuatro cuentas había cargado NUNCA una negociación ni subido una evidencia,
 * así que no se le quita a nadie algo que usara.
 *
 * ── PARA QUÉ SIRVE ESTA LISTA, SI `ROLES_ESCRIBEN_NEGO` YA FALLA CERRADO ───
 * En la página comercial NO hace falta: basta con no estar en la lista de
 * arriba. Hace falta en **Negociaciones Farmatodo** (`nego-ftd`), cuya alta
 * nunca tuvo candado de rol y la alcanzan roles que la de arriba no nombra
 * —FACTURACIONDDH y GERENCIAFINANZAS, por la sección Farmatodo—. Cerrarla con
 * la lista positiva se los habría quitado sin que nadie lo decidiera; por eso
 * allí se cierra SOLO a quien la usuaria nombró. Y hace falta como control:
 * db/140 §3.3 acepta un rol que alcance `nego` si está en una de las dos.
 *
 * Las dos listas NO pueden cruzarse (lo vigila `nego-permisos.test.ts`).
 */
export const ROLES_SOLO_MIRAN_NEGO: readonly string[] = [
  'ALMACENYDESPACHO', // db/140: abre la negociación para ver qué despachar
  'ANALISTATRADEMKT', // db/193: ANALISTATRADESENIOR y ANALISTATRADE
  'IYD',              // db/193: la página entra ese día, ya en solo lectura
  'CONSULTAGENERAL',  // db/273: la consulta general ve como MASTER y no carga nada
];

/** `true` si el usuario solo mira: tiene un rol de la lista y NINGUNO que
 *  escriba. Manda el rol que más puede, igual que en `puedeEscribirNego`. */
export function soloMiraNego(roles: readonly string[] | undefined): boolean {
  const r = roles ?? [];
  return r.some((x) => ROLES_SOLO_MIRAN_NEGO.includes(x)) && !puedeEscribirNego(r);
}

/**
 * QUIEN MIRA, PERO APRUEBA (2026-09-30). La usuaria eligió que TRADEMARKETING
 * **siga aprobando** las de Trade: es el único, además de MASTER, que las
 * decide (D5, `rolQueToca` en `negociaciones.mts`), y el 30/09 llevaba cuatro
 * aprobadas, la última el 25/09. Sacarle la decisión dejaba el circuito de
 * Trade en manos de MASTER solamente.
 *
 * Así que el candado general de solo lectura le deja pasar UNA sección, la
 * `decision`, y nada más: ni el alta, ni la anulación, ni la evidencia. No abre
 * de más porque esa rama lleva su propio candado (`candadoDecision`): solo
 * decide las de Trade, y nunca una que registró él mismo.
 *
 * Los analistas NO van aquí (db/164: «ninguno de los dos» aprueba).
 *
 * 🟢 VACÍA desde el 2026-10-15: TRADEMARKETING volvió a `ROLES_ESCRIBEN_NEGO`,
 * así que el candado general ya lo deja pasar por escribir y decide las de
 * Trade por `rolQueToca`, como antes de db/193. Se deja la lista (y su
 * función) en vez de borrarla porque es el mecanismo para el próximo «que mire
 * pero que apruebe»: basta con volver a escribir el rol aquí.
 */
export const ROLES_MIRAN_Y_DECIDEN_NEGO: readonly string[] = [];

/** Las secciones POST que `ROLES_MIRAN_Y_DECIDEN_NEGO` sí puede usar. */
export const SECCIONES_DE_QUIEN_MIRA_Y_DECIDE: readonly string[] = ['decision'];

/** `true` si el usuario, sin escribir, puede usar esa sección del endpoint. */
export function decideSinEscribirNego(
  roles: readonly string[] | undefined, seccion: string,
): boolean {
  return SECCIONES_DE_QUIEN_MIRA_Y_DECIDE.includes(seccion)
    && (roles ?? []).some((r) => ROLES_MIRAN_Y_DECIDEN_NEGO.includes(r));
}

/* ══════════════════════════════════════════════════════════════════════════
 * 🔴 LA RÉPLICA DE LA NC NO ES ESTA LISTA. Corrección del 2026-09-09.
 * ══════════════════════════════════════════════════════════════════════════
 * `ROLES_ESCRIBEN_NEGO` se midió arriba como «los roles que HOY alcanzan la
 * página `nego`», y el candado se puso contra CUALQUIER POST del endpoint. Las
 * dos frases no describen el mismo conjunto, y ahí estuvo el fallo:
 *
 *   `/api/negociaciones` sirve DOS páginas, no una. La comercial (`nego`) y la
 *   RÉPLICA de Notas de Crédito (`nego-nc`, db/072), que se pide con `vista=nc`
 *   y se valida contra su propia página.
 *
 * Y los de la réplica **no tienen `nego` a propósito**: db/079 se la quitó el
 * 2026-08-26 por pedido expreso de la usuaria («en el módulo de facturación
 * vamos a eliminar negociaciones comerciales … y solo vamos a dejar
 * negociaciones · notas de crédito»). O sea que los tres usuarios de
 * Facturación —los únicos que ESCRIBEN la NC— no podían salir jamás en una
 * lista medida sobre `nego`. La cabecera de db/140 hasta lo dice al revés, y
 * por eso nadie lo vio: «FACTURACION · FACTURACIONDDH la tienen EXCLUIDA por
 * rol … a SIETE de ellos esto no les cambia nada». Cierto para la PÁGINA;
 * falso para el ENDPOINT, que ellos siguen usando por la réplica.
 *
 * Lo que costó, medido en `auth.auditoria` el 2026-09-09:
 *   · del 26/08 al 07/09 cargaron 45 números de NC y 12 soportes (26+8
 *     FACTURACIONDDH, 19+4 FACTURACIONDHV);
 *   · db/140 se desplegó el 08/09 a las 08:36; la última carga que entró fue el
 *     07/09 a las 16:11 y desde entonces hay **0 cargas y 10 rebotes**
 *     (FACTURACIONDDH: cuatro el 08/09 por la tarde y seis el 09/09 a las 07:06
 *     y 07:09) con el 403 `NEGO_SOLO_LECTURA`. Un día y medio sin poder
 *     facturar la nota de crédito de ninguna negociación.
 *
 * ── POR QUÉ NO SE ARREGLA METIÉNDOLOS EN LA LISTA DE ARRIBA ────────────────
 * Porque esa lista abre los CINCO POST del módulo, y de esos cinco hay dos sin
 * candado propio: el ALTA y la EVIDENCIA. Meter a Facturación ahí les daría
 * registrar negociaciones y colgarles evidencia —justo lo que db/079 les quitó,
 * y algo que no han hecho nunca: 0 de 36 altas y 0 de todas las evidencias—.
 * El candado se arregla donde está roto, no ensanchando el permiso.
 */

/**
 * Quién ESCRIBE la nota de crédito. Es la lista de db/072, que vivía suelta
 * dentro de `negociaciones.mts` y sube aquí el 2026-09-09 justo por esto: las
 * dos listas de escritura de este módulo tienen que verse LA UNA A LA OTRA, o
 * vuelven a separarse en silencio.
 *
 *   · FACTURACION    — el rol de FACTURACIONDHV **y de FACTURACIONDIF**, que lo
 *                      comparten (solo se distinguen por su alcance de empresa).
 *                      La usuaria ratificó el 2026-08-26 que los dos escriben.
 *   · FACTURACIONDDH — el suyo propio (rol 18, db/044).
 *   · MASTER         — como en todo este módulo: es quien corrige un número mal
 *                      tecleado cuando Facturación no está.
 *
 *   · GERENCIAFINANZAS — desde el 2026-10-09 (Gerencia y Subgerencia de Finanzas,
 *                      que comparten rol). La usuaria: «deben ver, modificar y tener
 *                      acceso a todo lo que se encuentre en el módulo de finanzas»,
 *                      y la réplica de la NC es de ese módulo. Escribe la NC COMO
 *                      Facturación: el aviso al adjuntar sale igual, a los mismos
 *                      dos destinatarios. NO entra a `ROLES_ESCRIBEN_NEGO` (el alta y
 *                      la evidencia de la comercial): db/079 se la quitó por pedido
 *                      expreso y eso quedó PREGUNTADO, no decidido aquí.
 *
 * Los demás con la réplica —COBRANZAS y GERENCIACEI— LEEN.
 */
export const ROLES_CARGAN_NC: readonly string[] = ['MASTER', 'FACTURACION', 'FACTURACIONDDH', 'GERENCIAFINANZAS'];

/** `true` si alguno de los roles del usuario escribe la NC. Basta uno, por lo
 *  mismo que en `puedeEscribirNego`: manda el rol que más puede. */
export function puedeCargarNc(roles: readonly string[] | undefined): boolean {
  return (roles ?? []).some((r) => ROLES_CARGAN_NC.includes(r));
}

/**
 * LAS SECCIONES QUE SE CANDAN SOLAS, y por eso el candado general las salta.
 *
 * `?seccion=nc` (el número) y `?seccion=nc-archivo` (el soporte) comprueban
 * `puedeCargarNc` en su propia rama, y lo comprueban **más estrecho** que la
 * lista general: tres roles contra once. Dejarlas pasar por el candado general
 * no abre nada — quien no cargue la NC choca dos líneas más abajo, con el
 * mensaje que además dice la verdad («Solo Facturación puede cargar…») en vez
 * del genérico de solo lectura.
 *
 * ⚠️ ESTE CONJUNTO ES LA EXCEPCIÓN, NO LA REGLA, y por eso va escrito con
 * nombres y no con una condición. El candado general de db/140 existe porque
 * hoy hay cinco POST y el sexto que se añada mañana tiene que quedar cerrado
 * SIN que nadie se acuerde de él: cualquier sección que no esté aquí sigue
 * cubierta. Añadir un nombre a esta lista es decidir que esa rama se defiende
 * sola — y hay que poder señalar el `if` que lo hace.
 */
export const SECCIONES_CON_CANDADO_PROPIO: readonly string[] = ['nc', 'nc-archivo'];

/* ══════════════════════════════════════════════════════════════════════════
 * QUIÉN TRABAJA SOLO EL LADO DE TRADE (2026-09-17, db/164)
 * ══════════════════════════════════════════════════════════════════════════
 * Decide con qué TIPO se abren dos pantallas: la pestaña «Pendientes de
 * aprobación» y el presupuesto. No es un permiso —los dos tipos se ven igual—,
 * es dónde aterriza la vista.
 *
 * Nació el 2026-09-07 dentro de `Negociaciones.tsx` como `roles.includes(
 * 'TRADEMARKETING')`, con un motivo medido: el usuario de Trade Marketing abrió
 * «Pendientes» con dos de Trade esperando, la vio arrancar en Ventas —vacía— y
 * concluyó que las de Trade «no aparecen para su aprobación».
 *
 * Sale aquí porque desde db/164 son DOS roles y la condición se leía en DOS
 * componentes: repetida cuatro veces, la próxima cuenta de Trade se quedaría
 * fuera de alguna de ellas sin que nada fallara. Y **no dice «decide»** a
 * propósito, que es como se llamaba antes: los analistas NO deciden ninguna, y
 * aun así su trabajo está del lado de Trade — abrirles una tabla de 16 asesores
 * que no administran sería el mismo error del 07/09 con otro nombre.
 *
 * MASTER queda fuera: ve y decide los dos tipos, así que no tiene «su» lado.
 */
export const ROLES_SOLO_TRADE: readonly string[] = ['TRADEMARKETING', 'ANALISTATRADEMKT'];

/** `true` si el usuario trabaja el lado de Trade y solo ése. */
export function trabajaSoloTrade(roles: readonly string[] | undefined): boolean {
  const r = roles ?? [];
  return r.some((x) => ROLES_SOLO_TRADE.includes(x)) && !r.includes('MASTER');
}

/* ══════════════════════════════════════════════════════════════════════════
 * «NEGOCIACIONES TRADE» Y SOLO EL FORMATO DE TRADE (2026-10-29)
 * ══════════════════════════════════════════════════════════════════════════
 * La usuaria: «cuando el usuario sea de trade debe decir negociaciones trade
 * no negociaciones comerciales y que ellos puedan cargar sus negociaciones con
 * su formato … automaticamente debe cargar el formato de trade no el de ventas
 * o trade».
 *
 * Los mismos roles que `trabajaSoloTrade`, y la regla se lee en cuatro sitios:
 *   · el menú lateral (`Marco.tsx`) y la cabecera de la página rotulan la
 *     sección con `tituloNego`;
 *   · el formulario abre con el tipo FIJO en Trade, sin desplegable;
 *   · el servidor sirve en el catálogo solo el tipo TRADE —así el teléfono,
 *     que arma su desplegable con esa lista, tampoco ofrece Ventas— y rechaza
 *     un alta de Ventas con 422 `SOLO_TRADE` (un POST no pasa por la pantalla).
 * Medido ese día: ninguna de las tres cuentas de Trade había cargado nunca una
 * negociación de Ventas, así que el candado no deja nada a medias.
 */
export const TITULO_NEGO_TRADE = 'Negociaciones Trade';

/** Cómo se llama la sección para ese usuario: «Negociaciones Trade» para Trade. */
export function tituloNego(roles: readonly string[] | undefined): string {
  return trabajaSoloTrade(roles) ? TITULO_NEGO_TRADE : 'Negociaciones Comerciales';
}

/* ══════════════════════════════════════════════════════════════════════════
 * NEGOCIACIONES BBK: TRADE MARKETING VE SOLO EL REGISTRO (2026-10-15, db/245)
 * ══════════════════════════════════════════════════════════════════════════
 * La usuaria: «vamos a darle accesibilidad a las secciones de negociaciones
 * bbk solo el registro de las negociaciones bbk … a los tres usuarios de
 * trade» (TRADEMARKETING, ANALISTATRADESENIOR y ANALISTATRADE; los dos
 * analistas comparten `ANALISTATRADEMKT`, db/164).
 *
 * «Solo el registro» es más estrecho que «solo mirar» (`soloMiraNego`): quien
 * solo mira en la comercial ve además el presupuesto, y en BBK vería el
 * ACUMULADO y el REPORTE FINAL de cada negociación. Así que en `vista=bbk`
 * estos roles solo usan las secciones GET del registro:
 *   · ''          — el registro (la tabla y su Excel);
 *   · 'catalogos' — la página no pinta nada sin ellos (la cabecera y los
 *                   rótulos salen de ahí); no trae cifras de nadie;
 *   · 'evidencia', 'nc', 'nc-archivo' — lo que se abre DENTRO de una fila del
 *                   registro (las evidencias y la nota de crédito, en lectura).
 * Todo lo demás —'acumulado', 'reporte-bbk', 'buscar', 'cliente',
 * 'presupuesto' y cualquier sección que nazca mañana— responde 403
 * `NEGO_BBK_SOLO_REGISTRO`. Y ningún POST ni DELETE: la lista es de GET, así que
 * ni la `decision` que TRADEMARKETING conserva en la comercial (db/193) pasa
 * aquí (BBK no tiene aprobación: db/190 la responde con 409 de todas formas).
 *
 * Es una lista POSITIVA de secciones por la misma razón que el candado de
 * db/140: la sección que se añada mañana nace cerrada para ellos.
 *
 * MASTER queda fuera aunque tuviera además uno de estos roles: manda el rol
 * que más puede, como en `trabajaSoloTrade`.
 *
 * ⚠️ La app del teléfono lleva COPIA de la lista de roles (`api.js`,
 * `ROLES_BBK_SOLO_REGISTRO`) para esconder la ficha «Negociación BBK»; la
 * compara `nego-permisos.test.ts`.
 */
export const ROLES_BBK_SOLO_REGISTRO: readonly string[] = ['TRADEMARKETING', 'ANALISTATRADEMKT'];

/** Las secciones GET de `vista=bbk` que forman el REGISTRO. Ver arriba. */
export const SECCIONES_DEL_REGISTRO_BBK: readonly string[] = ['', 'catalogos', 'evidencia', 'nc', 'nc-archivo'];

/** `true` si en Negociaciones BBK el usuario ve solo el registro. */
export function soloRegistroBbk(roles: readonly string[] | undefined): boolean {
  const r = roles ?? [];
  return r.some((x) => ROLES_BBK_SOLO_REGISTRO.includes(x)) && !r.includes('MASTER');
}

/**
 * `true` si el usuario ESCRIBE en la vista que tiene delante (2026-10-15).
 *
 * Hasta hoy `puedeEscribirNego` bastaba también en BBK, porque los dos roles
 * de `ROLES_BBK_SOLO_REGISTRO` no escribían en ninguna parte. Al volver
 * TRADEMARKETING a `ROLES_ESCRIBEN_NEGO`, esa respuesta deja de valer en BBK:
 * allí sigue viendo solo el registro (db/245), y la pantalla le pintaría
 * «Nueva solicitud», el acumulado, el clip de las evidencias y el «Reporte»,
 * cuatro cosas que el servidor le contesta con 403 `NEGO_BBK_SOLO_REGISTRO`.
 */
export function puedeEscribirNegoEn(
  roles: readonly string[] | undefined, esBbk: boolean,
): boolean {
  return puedeEscribirNego(roles) && !(esBbk && soloRegistroBbk(roles));
}

/** `true` si el usuario puede usar esa sección de `vista=bbk` con ese método. */
export function puedeUsarSeccionBbk(
  roles: readonly string[] | undefined, metodo: string, seccion: string,
): boolean {
  if (!soloRegistroBbk(roles)) return true;
  return metodo === 'GET' && SECCIONES_DEL_REGISTRO_BBK.includes(seccion);
}

/* ══════════════════════════════════════════════════════════════════════════
 * FACTURACIONDHV VE SOLO LAS NEGOCIACIONES DE DHV (2026-11-03)
 * ══════════════════════════════════════════════════════════════════════════
 * La usuaria: «para el usuario de facturaciondhv que en las negociaciones y
 * promociones solo le aparezca la informacion relacionada con dhv no la de ddh
 * o la de dif». Medido ese día en producción: en la réplica de la NC le
 * llegaban 152 de Ventas (7 de DHV), 19 de Trade (1) y 64 de BBK (1).
 *
 * VA POR NOMBRE DE USUARIO, NO POR EL ALCANCE DE EMPRESA, y es a propósito.
 * Los tres de Facturación tienen su fila EMPRESA en `auth.usuario_alcance`
 * (DDH 0, DHV 1, DIF 2), pero aplicar ese eje a todos le quitaría a
 * FACTURACIONDDH las negociaciones de Difranca, que son SUYAS: anotó la NC de
 * las 21 de DIF que la tienen (y de una de DHV). Difranca no tiene Facturación
 * propia en el circuito —es el mismo usuario de DDH, como en Devoluciones—, así
 * que el eje solo dice la verdad para esta cuenta. Es el trato de
 * `USUARIOS_ACCION` en Devoluciones y de `REGIONAL_LECTURA_NACIONAL`.
 *
 * Recorta en `alcanceNegociacion` (`negociaciones.mts`): el registro de las
 * tres vistas, sus contadores y TODA ruta por id —evidencia y NC—, así que una
 * de DDH no se abre tecleando su id. Promociones ya venía recortada por
 * empresa (`FACTURACION_EMPRESA` en `promos-acceso.ts`).
 */
export const NEGO_SOLO_SU_EMPRESA: Readonly<Record<string, 'DDH' | 'DHV' | 'DIF'>> = {
  FACTURACIONDHV: 'DHV',
};

/** La única empresa (`empresas_txt`) cuyas negociaciones ve ese usuario, o `null` si no tiene ese tope. */
export function empresaUnicaNego(username: string | undefined): 'DDH' | 'DHV' | 'DIF' | null {
  return NEGO_SOLO_SU_EMPRESA[(username ?? '').toUpperCase()] ?? null;
}
